import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useI18n } from '@/i18n/context';
import { cn } from '@/lib/cn';

/**
 * Page title. The heading receives focus when the screen opens so keyboard
 * and screen-reader users start at the top of the new content.
 */
export function ScreenHeader({
  title,
  subtitle,
  back,
  actions,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Path to go back to, or true for browser history. */
  back?: string | true;
  actions?: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);

  return (
    <header className={cn('flex items-start gap-3', className)}>
      {back && (
        <button
          type="button"
          onClick={() => (back === true ? navigate(-1) : navigate(back))}
          className="no-print mt-1 inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-surface text-ink shadow-[var(--shadow-card)] hover:bg-surface-muted"
          aria-label={t('app.back')}
        >
          <ArrowRight aria-hidden className="size-5 ltr:-scale-x-100" />
        </button>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h1
          ref={ref}
          tabIndex={-1}
          className="text-2xl leading-tight font-black outline-none sm:text-3xl"
        >
          {title}
        </h1>
        {subtitle && <div className="text-ink-muted">{subtitle}</div>}
      </div>
      {actions && <div className="no-print flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}
